import React, { useState } from "react";
import { Button } from "../../../../design/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../../../../design/dialog";
import { Input } from "../../../../design/input";
import {
  Field,
  Fieldset,
  FieldGroup,
  Label,
  ErrorMessage,
} from "../../../../design/fieldset";
import { useIntakeQuestion } from "../../../../contexts/IntakeQuestionContext";
import { Checkbox, CheckboxField } from "../../../../design/checkbox";
import { MatterType } from "../../../../models/MatterType";

interface Props {
  children?: React.ReactNode;
  onTaskCreated?: (taskId: string) => void;
  matterType: MatterType;
}

interface Errors {
  question?: string;
}

const CreateIntakeQuestionButton: React.FC<Props> = ({
  matterType,
  children,
  onTaskCreated,
}) => {
  const { createIntakeQuestion } = useIntakeQuestion();
  const [isOpen, setIsOpen] = useState(false);
  const [question, setQuestion] = useState<string>("");
  const [errors, setErrors] = useState<Errors>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isCreatingMore, setIsCreatingMore] = useState(false);

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    if (!question.trim()) {
      newErrors.question = "A question is required.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    setIsSaving(true);
    try {
      const taskId = await createIntakeQuestion({
        question: question,
        matterType: matterType._id,
        index: 0,
      });
      if (onTaskCreated) {
        onTaskCreated(taskId);
      }
      clearForm(isCreatingMore);
    } catch (error) {
      console.error("Error creating location:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const clearForm = (blockClose: boolean) => {
    setQuestion("");
    if (!blockClose) {
      setIsOpen(false);
    }
  };

  return (
    <>
      <Button onClick={() => setIsOpen(true)}>{children}</Button>
      <Dialog open={isOpen} onClose={setIsOpen}>
        <DialogTitle>Create Task</DialogTitle>
        <DialogBody>
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>Text</Label>
                <Input
                  type="text"
                  value={question}
                  placeholder="Question"
                  onChange={(e) => setQuestion(e.target.value)}
                />
                {errors.question && (
                  <ErrorMessage>{errors.question}</ErrorMessage>
                )}
              </Field>
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions className="justify-between">
          <CheckboxField>
            <Checkbox
              checked={isCreatingMore}
              onChange={(checked) => setIsCreatingMore(checked)}
            />
            <Label>Create more</Label>
          </CheckboxField>
          <div className="flex gap-2">
            <Button plain onClick={() => clearForm(false)}>
              Cancel
            </Button>
            <Button disabled={isSaving} onClick={handleSubmit}>
              {isSaving ? "Saving..." : "Save"}
            </Button>
          </div>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CreateIntakeQuestionButton;
